/**
 * Student Document Studio — Canonical Document AST Schema (Version 1.0.0)
 * 
 * This model is the single source of truth across:
 * - Live Paginated Preview
 * - Vector PDF Generation (pdf-lib)
 * - OpenXML DOCX Compilation
 * - Offline Storage & Local Persistence
 */

export const CURRENT_SCHEMA_VERSION = 1;

export type PaperSize = 'A4' | 'Letter' | 'Legal';
export type PageOrientation = 'portrait' | 'landscape';

export interface PageMargins {
  topMm: number;    // Standard: 25.4mm (1.0 inch)
  bottomMm: number; // Standard: 25.4mm
  leftMm: number;   // Standard: 25.4mm
  rightMm: number;  // Standard: 25.4mm
}

export interface DocumentMetadata {
  id: string;
  title: string;
  subtitle?: string;
  documentType: 'lab_report' | 'assignment' | 'cover_page' | 'project' | 'custom';
  courseCode?: string;
  courseTitle?: string;
  author: {
    studentName: string;
    studentId?: string;
    email?: string;
    sectionOrGroup?: string;
  };
  instructor: {
    instructorName?: string;
    designation?: string;
    department?: string;
  };
  institution: {
    name: string;
    shortName: string;
    department?: string;
    logoAssetId?: string;
    website?: string;
  };
  createdAt: string;  // ISO timestamp
  modifiedAt: string; // ISO timestamp
  submissionDate?: string;
  version: number;
}

export interface PageSettings {
  paperSize: PaperSize;
  orientation: PageOrientation;
  margins: PageMargins;
  defaultFontFamily: string;
  defaultFontSizePt: number;
  defaultLineSpacing: number;
  showPageNumbers: boolean;
  pageNumberPosition: 'bottom-center' | 'bottom-right' | 'top-right';
  headerText?: string;
  footerText?: string;
}

export type SectionType = 
  | 'cover'
  | 'title'
  | 'abstract'
  | 'introduction'
  | 'objectives'
  | 'theory'
  | 'apparatus'
  | 'procedure'
  | 'data'
  | 'calculations'
  | 'results'
  | 'discussion'
  | 'conclusion'
  | 'references'
  | 'custom';

export interface SectionSettings {
  pageBreakBefore?: boolean;
  numbered?: boolean;
  headingLevel?: 1 | 2 | 3;
}

export type ShapeType =
  | 'rectangle'
  | 'rounded_rectangle'
  | 'circle'
  | 'ellipse'
  | 'line'
  | 'arrow'
  | 'triangle'
  | 'divider';

export type BlockType = 
  | 'text'
  | 'formula'
  | 'table'
  | 'image'
  | 'shape'
  | 'page_break';

export interface BaseBlock {
  id: string;
  type: BlockType;
  createdAt: string;
}

export interface TextFormatting {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  subscript?: boolean;
  superscript?: boolean;
  alignment?: 'left' | 'center' | 'right' | 'justify';
  fontFamily?: string;
  fontSizePt?: number;
  color?: string;
  backgroundColor?: string;
  lineHeightMultiplier?: number;
  letterSpacingPx?: number;
  headingLevel?: 1 | 2 | 3 | 4 | 'paragraph';
  offsetX?: number;
  offsetY?: number;
  rotation?: number;
  opacity?: number;
}

export interface TextBlockData {
  text: string;
  formatting?: TextFormatting;
  level?: 'paragraph' | 'heading1' | 'heading2' | 'heading3' | 'heading4';
}

export interface TextBlock extends BaseBlock {
  type: 'text';
  data: TextBlockData;
}

export interface FormulaBlockData {
  latex: string;
  displayMode: boolean; // true = block-centered, false = inline
  caption?: string;
  equationNumber?: string; // e.g. "(1)"
  fontSizePt?: number;
  alignment?: 'left' | 'center' | 'right';
  offsetX?: number;
  offsetY?: number;
}

export interface FormulaBlock extends BaseBlock {
  type: 'formula';
  data: FormulaBlockData;
}

export interface TableCell {
  id: string;
  content: string;
  isHeader?: boolean;
  alignment?: 'left' | 'center' | 'right';
  bold?: boolean;
  widthPercent?: number;
}

export interface TableBlockData {
  rows: number;
  cols: number;
  hasHeaderRow: boolean;
  hasHeaderCol: boolean;
  cells: TableCell[][];
  caption?: string;
  borderStyle?: 'grid' | 'academic' | 'minimal' | 'none'; // 'academic' = APA style with top/bottom/header borders only
  alignment?: 'left' | 'center' | 'right';
  offsetX?: number;
  offsetY?: number;
}

export interface TableBlock extends BaseBlock {
  type: 'table';
  data: TableBlockData;
}

export interface ImageBlockData {
  assetId: string;        // ID reference in the AssetRepository
  previewDataUrl?: string;// Cached preview blob URL / Data URL
  caption?: string;
  altText?: string;
  widthPercent: number;   // 10 to 100 (%)
  alignment: 'left' | 'center' | 'right';
  opacity?: number;
  rotation?: number;
  offsetX?: number;
  offsetY?: number;
}

export interface ImageBlock extends BaseBlock {
  type: 'image';
  data: ImageBlockData;
}

export interface ShapeBlockData {
  shapeType: ShapeType;
  fill: string;
  stroke: string;
  strokeWidth: number;
  strokeStyle?: 'solid' | 'dashed' | 'dotted';
  opacity?: number;
  width: number;
  height: number;
  rotation?: number;
  alignment?: 'left' | 'center' | 'right';
  offsetX?: number;
  offsetY?: number;
}

export interface ShapeBlock extends BaseBlock {
  type: 'shape';
  data: ShapeBlockData;
}

export interface PageBreakBlockData {
  forced: boolean;
}

export interface PageBreakBlock extends BaseBlock {
  type: 'page_break';
  data: PageBreakBlockData;
}

export type DocumentBlock = 
  | TextBlock 
  | FormulaBlock 
  | TableBlock 
  | ImageBlock 
  | ShapeBlock 
  | PageBreakBlock;

export interface DocumentSection {
  id: string;
  title: string;
  type: SectionType;
  enabled: boolean;
  collapsed?: boolean;
  settings?: SectionSettings;
  blocks: DocumentBlock[];
}

export interface DocumentModel {
  schemaVersion: number;
  metadata: DocumentMetadata;
  pageSettings: PageSettings;
  sections: DocumentSection[];
}
